import type {FunctionComponent} from 'react';
import React from 'react';
import {LandingGoToButton, LandingPage} from '@tempo/landing';
import {Box} from '@tempo/core';
import {useRouter} from 'next/dist/client/router';
import dynamic from 'next/dynamic';
import {NextSeo} from 'next-seo';
import Header from './tempo-header';
import metas from 'locales/metas.json';
import {StructuredData} from '@app/views/structuredData';

const LazyFooter = dynamic(() => import('./tempo-footer'), {
	loading: () => <footer />,
});

type TempoPageProps = {
	initialMode?: 'dark' | 'light';
	title: string;
	description?: string;
	openedLocales?: string[];
	footer?: React.ReactNode;
	header?: React.ReactNode;
	goToBtn?: React.ReactNode;
	withBreadCrumbStructuredData?: boolean;
};

export const TempoLandingPage: FunctionComponent<
	React.PropsWithChildren<TempoPageProps>
> = ({
	children,
	title,
	description,
	header,
	footer,
	goToBtn = <LandingGoToButton zIndex={2} />,
	openedLocales,
	withBreadCrumbStructuredData,
}) => {
	const {locales, query} = useRouter();

	const insideApp = query.insideApp === '1';

	return (
		<>
			<NextSeo
				title={title}
				description={description}
				noindex={!description}
				nofollow={!description}
				openGraph={
					description
						? {
								title: title,
								description: description,
							}
						: undefined
				}
			/>
			<StructuredData
				description={description}
				withBreadCrumbStructuredData={withBreadCrumbStructuredData}
			/>
			<LandingPage data-cy="landingpage">
				{goToBtn}
				<Box data-cy="landingpage-content">
					{!insideApp && (header ?? <Header />)}
					{children}
				</Box>
				{!insideApp &&
					(footer ?? (
						<LazyFooter
							openedLocales={
								openedLocales ??
								locales.filter((lang) => !metas.sunsetLanguages.includes(lang))
							}
						/>
					))}
			</LandingPage>
		</>
	);
};
